import { DedicatedPackageOutput } from '@allocab/allocab-dedicated-driver-types';
import { CarSolid, ClockSolid, LocationDotSolid } from '@allocab/allocab-icons';

import { Link } from '../../components/atomic/Link';
import { BadgeIndicator } from '../../components/BadgeIndicator';
import { TripPackageAssignButton } from '../../components/trip-package-actions-button/TripPackageAssignButton';
import { TripPackageRefuseButton } from '../../components/trip-package-actions-button/TripPackageRefuseButton';
import { useDriverOptions } from '../../services/driver-options/useDriverOptions';
import { t } from '../../services/translation/translation';
import { getFullName } from '../../utils/common';
import { formatDateForParisTZ } from '../../utils/date';
import { getTripPackageStatus, getTripPackageStatusVariant } from '../../utils/package';
import { formatPrice, formatPriceOptions } from '../../utils/price';
import { shortenFormattedAddress } from '../../utils/trip';

type Props = {
  trippackage: DedicatedPackageOutput;
};

export const TripPackageCard = ({ trippackage }: Props) => {
  const { driverOptions } = useDriverOptions();

  return (
    <div className="max-w-full p-3 bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700 space-y-1">
      <div className="text-xs flex flex-row justify-between items-center gap-1">
        <div className="flex flex-col items-start">
          <div className="space-x-3">
            <BadgeIndicator variant={getTripPackageStatusVariant(trippackage)}>
              {getTripPackageStatus(trippackage)}
            </BadgeIndicator>
            <span>{trippackage.id}</span>
          </div>
        </div>
      </div>

      <div className="flex gap-1">
        <div className="flex items-center lg:flex-row lg:items-start font-bold flex-col gap-1 p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600 text-center">
          {trippackage.totalPrice
            ? formatPrice(
                driverOptions.isSubjectToVAT ? trippackage.totalPrice?.amountWithTax : trippackage.totalPrice?.amount,
              )
            : null}{' '}
          <p className="text-[10px] opacity-50 font-normal">
            {trippackage.totalPrice ? formatPriceOptions(driverOptions) : null}
          </p>
          (
          {formatPrice(
            driverOptions.isSubjectToVAT ? trippackage.hourlyRate.amountWithTax : trippackage.hourlyRate.amount,
          )}
          /h)
        </div>
        <div className="p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600 flex items-center gap-1">
          <LocationDotSolid width={14} height={14} className="fill-current" />
          <p className="flex-1 min-w-0">
            {shortenFormattedAddress(trippackage.referenceAddress.formattedAddress || '--')}
          </p>
        </div>
      </div>

      <div className="flex items-stretch  gap-1">
        <div className="p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600 flex-1">
          <div className="flex items-center gap-2">
            <ClockSolid width={12} height={12} className="fill-orange-400" />
            <p className="flex-1 min-w-0 text-ellipsis overflow-hidden">
              {formatDateForParisTZ(trippackage.startDate, t('date.datetime'))}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ClockSolid width={12} height={12} className="fill-current" />
            <p className="flex-1 min-w-0">{formatDateForParisTZ(trippackage.endDate, t('date.datetime'))}</p>
          </div>
        </div>

        <div className="flex items-stretch gap-1">
          <div className="flex items-center gap-2 flex-1 p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600">
            <CarSolid className="fill-current" width={13} height={13} />
            <div className={'italic text-red-600'}>
              {trippackage.driver ? (
                <Link to={`/drivers/${trippackage.driver.key}`}>
                  {getFullName(trippackage.driver.firstName, trippackage.driver.lastName)}
                </Link>
              ) : (
                <div>{t('common.no-driver')}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div>
        {trippackage.status === 'PROPOSED' && trippackage.driver ? (
          <div className="flex justify-center space-x-3">
            <TripPackageAssignButton tripPackage={trippackage} driverKey={trippackage.driver?.key} />
            <TripPackageRefuseButton tripPackage={trippackage} driverKey={trippackage.driver?.key} />
          </div>
        ) : null}
      </div>
    </div>
  );
};
